import { useState } from 'react';
import { api } from '../lib/api';

/** Respuesta de GET /platform/cfdi/pac-probe (T2b, C-04). */
export interface PacProbeResult { ok: boolean; status: number | null; message: string | null }

/** Texto y color del resultado de la sonda: 401/403 = la llave; otro error = el PAC. */
export function pacProbeView(r: PacProbeResult): { tone: 'ok' | 'key' | 'pac'; text: string } {
  if (r.ok) return { tone: 'ok', text: 'OK — el PAC acepta la llave de FluidSol.' };
  if (r.status === 401 || r.status === 403) {
    return { tone: 'key', text: `La llave fue rechazada (HTTP ${r.status}). Revisa FISCALAPI_API_KEY / TENANT_KEY / BASE_URL del backend.` };
  }
  return {
    tone: 'pac',
    text: `El PAC no respondió bien (${r.status ? `HTTP ${r.status}` : 'sin respuesta'}). No es la llave: vuelve a intentar en unos minutos.`,
  };
}

const TONE_CLS = {
  ok: 'bg-emerald-50 text-emerald-700',
  key: 'bg-rose-50 text-rose-700',
  pac: 'bg-amber-50 text-amber-700',
} as const;

/**
 * T2b (C-04) — botón "Probar llave del PAC" en /platform → CFDI Ventas. Hace la misma
 * llamada que el timbrado antes de cada sello (solo lectura); la sonda diaria del backend
 * usa la misma.
 */
export function PacProbeCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PacProbeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [at, setAt] = useState<string | null>(null);

  const run = async () => {
    setBusy(true); setError(null);
    try {
      setResult((await api('/platform/cfdi/pac-probe')) as PacProbeResult);
      setAt(new Date().toLocaleTimeString('es-MX'));
    } catch (e) {
      const err = e as { body?: { message?: string }; message?: string };
      setError(err?.body?.message || err?.message || 'No se pudo correr la sonda');
    } finally { setBusy(false); }
  };

  const view = result ? pacProbeView(result) : null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Llave del PAC</h4>
          <p className="text-xs text-slate-500">Prueba si FiscalAPI acepta la llave de FluidSol (solo lectura, no timbra).</p>
        </div>
        <button onClick={run} disabled={busy}
          className="rounded-xl bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-50">
          {busy ? 'Probando…' : 'Probar llave del PAC'}
        </button>
      </div>
      {view && (
        <p className={`mt-3 rounded-lg px-3 py-2 text-sm ${TONE_CLS[view.tone]}`}>
          {view.text}{at ? ` (${at})` : ''}
        </p>
      )}
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
    </div>
  );
}
